import type { PodMetrics, PodModel } from "../../types";

/** Helpers over a pod's own GET /metrics (serving_backend core/monitoring.py). */

/** the routes the backend's evaluation calls, in pipeline order (crust, phase 2, the older phase 2) */
export const INFERENCE_ROUTES = ["/pizza/raw", "/analyze/raw", "/components/raw"] as const;

/** the pod's bucket of unknown paths (internet scanners hit the public pod ports) */
export const OTHER_ROUTE = "other";

/** stage timers that ran outside any request (model load, warm-up) */
export const STARTUP_STAGES = "startup";

/** throttle reasons that cost speed; sw_power_cap (the card at its power limit) is normal under load */
export const SLOWING_THROTTLE: ReadonlySet<string> = new Set(["hw_slowdown", "sw_thermal", "hw_thermal", "hw_power_brake"]);

/** ONNX Runtime falls back to the CPU silently: on the GPU = a CUDA provider (ORT) or a "cuda…" device (torch) */
export const runsOnGpu = (model: PodModel) =>
  model.providers.some((provider) => provider === "CUDAExecutionProvider" || provider.startsWith("cuda"));

/** loaded, but not on the GPU (10-50x slower) */
export const runsOnCpu = (model: PodModel) => model.loaded && !runsOnGpu(model);

export type GpuColumn = "util_pct" | "memory_used_mb" | "temperature_c" | "power_w";

/** one column of the 15 min GPU history (oldest first) */
export function gpuHistory(metrics: PodMetrics, column: GpuColumn): (number | null)[] {
  const history = metrics.history;
  const index = history?.gpu_columns.indexOf(column) ?? -1;
  if (!history || index < 0) return [];
  return history.gpu.map((row) => row[index] ?? null);
}

/** "Gen4 x16" */
export const pcieLink = (gen: number | null | undefined, width: number | null | undefined) =>
  [gen ? `Gen${gen}` : null, width ? `x${width}` : null].filter(Boolean).join(" ");

/** the 5 min max of the pod's event-loop lag */
export const loopMax5m = (metrics: PodMetrics) => metrics.loop?.windows?.["5m"]?.max_ms ?? null;
